// 2026-07-08 - FormuLearning - 관리자: 사용자·권한 관리 (REQ-FN-ADMIN-06, 이슈 #31)
// 2026-11-03 수정 - 이슈 #114: 역할 선택지에 viewer.
// 파일 위치: frontend/src/features/admin/UserManageList.tsx
// 사용자 목록·생성·수정(이름/역할/활성)·비밀번호 재설정·비활성화(소프트 삭제).

import { useEffect, useState } from 'react'
import { listAdminUsers, createUser, updateUser, deactivateUser } from '../../api/admin'
import type { AdminUserManage, UserRole } from '../../types/admin'
import { useAuthStore } from '../../stores/authStore'

const ROLES: UserRole[] = ['researcher', 'admin', 'viewer'] // viewer = 조회 전용(#114)

function errMessage(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg)
  return '요청을 처리하지 못했습니다.'
}

const inputCls = 'rounded border bg-bg-input px-2 py-1 text-sm text-text-body'
const divider = { borderColor: 'var(--border-divider)' }

export default function UserManageList() {
  const me = useAuthStore((s) => s.user)
  const [rows, setRows] = useState<AdminUserManage[]>([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [creating, setCreating] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)

  // 생성 폼
  const [cEmail, setCEmail] = useState('')
  const [cName, setCName] = useState('')
  const [cPass, setCPass] = useState('')
  const [cRole, setCRole] = useState<UserRole>('researcher')

  // 편집 폼
  const [eName, setEName] = useState('')
  const [eRole, setERole] = useState<UserRole>('researcher')
  const [eActive, setEActive] = useState(true)
  const [ePass, setEPass] = useState('')

  async function load() {
    setLoading(true)
    try {
      setRows(await listAdminUsers())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  async function submitCreate() {
    setErr('')
    try {
      await createUser({ email: cEmail.trim(), name: cName.trim(), password: cPass, role: cRole })
      setCreating(false); setCEmail(''); setCName(''); setCPass(''); setCRole('researcher')
      await load()
    } catch (e) {
      setErr(errMessage(e))
    }
  }

  function startEdit(u: AdminUserManage) {
    setErr(''); setEditId(u.id); setEName(u.name); setERole(u.role as UserRole); setEActive(u.is_active); setEPass('')
  }

  async function submitEdit(id: string) {
    setErr('')
    try {
      await updateUser(id, {
        name: eName.trim(),
        role: eRole,
        is_active: eActive,
        password: ePass ? ePass : undefined,
      })
      setEditId(null)
      await load()
    } catch (e) {
      setErr(errMessage(e))
    }
  }

  async function toggleActive(u: AdminUserManage) {
    setErr('')
    try {
      if (u.is_active) await deactivateUser(u.id)
      else await updateUser(u.id, { is_active: true })
      await load()
    } catch (e) {
      setErr(errMessage(e))
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-secondary">{rows.length} users</span>
        <button
          onClick={() => { setCreating((v) => !v); setErr('') }}
          className="rounded px-3 py-1.5 text-xs text-text-body hover:bg-bg-input"
          style={{ border: '1px solid var(--border-divider)' }}
        >
          {creating ? 'Cancel' : '+ New user'}
        </button>
      </div>

      {err && (
        <div className="rounded px-3 py-2 text-xs" style={{ backgroundColor: 'var(--bg-chip-warn, rgba(220,80,80,0.12))', color: 'var(--color-warn, #d05050)' }}>
          {err}
        </div>
      )}

      {creating && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border p-3" style={divider}>
          <label className="flex flex-col gap-1 text-xs text-text-secondary">Email
            <input value={cEmail} onChange={(e) => setCEmail(e.target.value)} className={inputCls} style={divider} placeholder="user@lab.dev" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-secondary">Name
            <input value={cName} onChange={(e) => setCName(e.target.value)} className={inputCls} style={divider} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-secondary">Password (≥8)
            <input type="password" value={cPass} onChange={(e) => setCPass(e.target.value)} className={inputCls} style={divider} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-secondary">Role
            <select value={cRole} onChange={(e) => setCRole(e.target.value as UserRole)} className={inputCls} style={divider}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <button
            onClick={submitCreate}
            disabled={!cEmail.trim() || !cName.trim() || cPass.length < 8}
            className="rounded px-3 py-1.5 text-xs text-white disabled:opacity-40"
            style={{ backgroundColor: 'var(--color-accent-teal)' }}
          >
            Create
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border" style={divider}>
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-text-secondary" style={{ backgroundColor: 'var(--color-bg-panel)' }}>
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium" title="소유 프로젝트 수">Projects</th>
              <th className="px-3 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const isSelf = me?.id === u.id
              return editId === u.id ? (
                <tr key={u.id} className="border-t" style={{ ...divider, backgroundColor: 'var(--color-bg-panel)' }}>
                  <td className="px-3 py-2">
                    <input value={eName} onChange={(e) => setEName(e.target.value)} className={inputCls} style={divider} />
                  </td>
                  <td className="px-3 py-2 text-text-secondary">{u.email}</td>
                  <td className="px-3 py-2">
                    <select value={eRole} onChange={(e) => setERole(e.target.value as UserRole)} disabled={isSelf} className={inputCls} style={divider}>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={eActive} disabled={isSelf} onChange={(e) => setEActive(e.target.checked)} />
                  </td>
                  <td className="px-3 py-2" colSpan={1}>
                    <input type="password" value={ePass} onChange={(e) => setEPass(e.target.value)} placeholder="새 비번(선택)" className={`${inputCls} w-32`} style={divider} />
                  </td>
                  <td className="px-3 py-2 text-text-secondary">{u.project_count}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => submitEdit(u.id)} className="rounded px-2 py-1 text-xs text-white" style={{ backgroundColor: 'var(--color-accent-teal)' }}>Save</button>
                    <button onClick={() => setEditId(null)} className="ml-2 rounded px-2 py-1 text-xs text-text-secondary hover:bg-bg-input">Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={u.id} className="border-t" style={divider}>
                  <td className="px-3 py-2 text-text-body">{u.name}{isSelf && <span className="ml-1 text-xs text-text-secondary">(you)</span>}</td>
                  <td className="px-3 py-2 text-text-secondary">{u.email}</td>
                  <td className="px-3 py-2 text-text-body">{u.role}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs" style={{ color: u.is_active ? 'var(--color-accent-teal)' : 'var(--color-text-secondary)' }}>
                      {u.is_active ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-text-secondary">{u.project_count}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(u)} className="rounded px-2 py-1 text-xs text-text-body hover:bg-bg-input" style={{ border: '1px solid var(--border-divider)' }}>Edit</button>
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={isSelf && u.is_active}
                      title={isSelf && u.is_active ? '자기 자신은 비활성화할 수 없습니다' : ''}
                      className="ml-2 rounded px-2 py-1 text-xs text-text-secondary hover:bg-bg-input disabled:opacity-40"
                      style={{ border: '1px solid var(--border-divider)' }}
                    >
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-text-secondary">No users.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
